import { ColumnDef } from '@tanstack/react-table';
import type { ServiceOrderListEntry } from 'contracts/service-orders';
import { DataTableHelper, Formatter } from 'utils';
import { PriorityIndicator } from 'pages/stock-inspection/shared-components';

import {
  TasksListPopover,
  StatusIndicator,
  DeadlineOverdueIndicator,
} from '../components';

const dt = new DataTableHelper<ServiceOrderListEntry>();

export const useServiceOrderColumns = (): ColumnDef<ServiceOrderListEntry>[] => {
  const priorityIndicator = dt.buildColumn({
    id: 'Prior.',
    size: 100,
    accessorFn: (row) => row.meta.highestProductPriority,
    content: (row) => <PriorityIndicator priority={row.meta?.highestProductPriority} />,
  });
  const orderNumber = dt.buildColumn({
    id: 'Número da OS',
    size: 120,
    accessorFn: (row) => row.id,
    content: (row) => {
      const seqNumber = row.id.toString().padStart(4, '0');
      const year = row.createdAt ? new Date(row.createdAt).getFullYear() : new Date().getFullYear();
      const yearShort = year.toString().slice(-2);
      return `${seqNumber}/${yearShort}`;
    },
  });
  const createdAt = dt.buildColumn({
    id: 'Data',
    size: 120,
    accessorKey: 'createdAt',
    content: (row) => Formatter.date(row.createdAt),
  });
  const deadline = dt.buildColumn({
    id: 'Prazo',
    size: 120,
    accessorKey: 'deadlineDays',
    content: (row) =>
      row.deadlineDays !== null ? `${row.deadlineDays} d` : '---',
  });
  const invoiceNumber = dt.buildColumn({
    id: 'Número da NF',
    size: 200,
    accessorKey: 'invoiceNumber',
    content: (row) => row.invoiceNumber ?? '---',
  });
  const plant = dt.buildColumn({
    id: 'Planta',
    size: 120,
    accessorKey: 'plant',
    content: (row) => row.plant,
  });
  const destinationPlant = dt.buildColumn({
    id: 'Planta Destino',
    size: 120,
    accessorKey: 'destinationPlant',
    content: (row) => row.destinationPlant ?? '---',
  });
  const storageLocation = dt.buildColumn({
    id: 'SLOC',
    size: 120,
    accessorKey: 'storageLocation',
    content: (row) => row.storageLocation,
  });
  const material = dt.buildColumn({
    id: 'Código',
    size: 120,
    accessorKey: 'material',
    content: (row) => row.material,
  });
  const materialDescription = dt.buildColumn({
    id: 'Produto',
    size: 400,
    accessorKey: 'description',
    content: (row) => (
      <div className="w-[210px] truncate" title={row.description}>
        {row.description}
      </div>
    ),
  });
  const usdValue = dt.buildColumn({
    id: 'Valor USD',
    size: 120,
    accessorKey: 'totalValue',
    content: (row) =>
      row.totalValue ? Formatter.currency(row.totalValue, 'USD') : '---',
  });
  const tasks = dt.buildColumn({
    id: 'Trabalhos',
    size: 120,
    accessorFn: (row) => row.tasks,
    content: (row) => <TasksListPopover tasks={row.tasks} />,
  });
  const startDate = dt.buildColumn({
    id: 'Início',
    size: 120,
    accessorKey: 'startDate',
    content: (row) =>
      row.startDate ? Formatter.date(row.startDate) : '--/--/----',
  });
  const endDate = dt.buildColumn({
    id: 'Término',
    size: 120,
    accessorKey: 'endDate',
    content: (row) =>
      row.endDate ? Formatter.date(row.endDate) : '--/--/----',
  });
  const indicators = dt.buildColumn({
    id: 'Indicadores',
    size: 100,
    title: '',
    content: (row) => (
      <div className={'flex items-center justify-center gap-4'}>
        <DeadlineOverdueIndicator serviceOrder={row} />
        <StatusIndicator status={row.currentStatus} />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  });

  return [
    priorityIndicator,
    orderNumber,
    createdAt,
    deadline,
    invoiceNumber,
    plant,
    destinationPlant,
    storageLocation,
    material,
    materialDescription,
    usdValue,
    tasks,
    startDate,
    endDate,
    indicators,
  ];
};
